import React from "react";
import { InlineMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import MaturaPiecewiseInequalityExplanationGraph from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaPiecewiseInequalityExplanationGraph";
import {
  randInt,
  parseSingleInterval,
  parseIntervalsUnion,
  getIntervalRaw,
  intervalsEqualList,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

const RELATIONS = [
  { latex: "f(x)>0", operator: ">", includeZeros: false, wantsPositive: true },
  { latex: "f(x)<0", operator: "<", includeZeros: false, wantsPositive: false },
  { latex: "f(x)\\ge 0", operator: ">=", includeZeros: true, wantsPositive: true },
  { latex: "f(x)\\le 0", operator: "<=", includeZeros: true, wantsPositive: false },
];

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function makeInterval(left, right, leftClosed, rightClosed) {
  return { left, right, leftClosed, rightClosed };
}

function sampleFunction(fn, xL, xR, importantXs) {
  const xs = [];
  const step = 0.05;

  for (let x = xL; x <= xR + 1e-9; x += step) {
    xs.push(Number(x.toFixed(6)));
  }
  importantXs.forEach((x) => xs.push(x));
  const uniqueXs = Array.from(new Set(xs)).sort((a, b) => a - b);

  return {
    x: uniqueXs,
    y: uniqueXs.map((x) => fn(x)),
    color: "orange",
    width: 6,
  };
}

function tryBuildData() {
  const relation = RELATIONS[randInt(0, RELATIONS.length - 1)];
  const twoZeros = Math.random() < 0.55;
  const xL = randInt(-5, -4);
  const xR = randInt(4, 5);
  const leftClosed = pickClosedFlag();
  const rightClosed = pickClosedFlag();
  const orientation = Math.random() < 0.5 ? -1 : 1;

  let zeros;
  let graphKind;
  let rawFunction;

  if (twoZeros) {
    const z1 = randInt(xL + 1, -1);
    const z2 = randInt(Math.max(z1 + 2, 1), xR - 1);
    zeros = [z1, z2];
    graphKind = "quadratic";
    rawFunction = (x) => orientation * (x - z1) * (x - z2);
  } else {
    const zeroX = randInt(xL + 2, xR - 2);
    zeros = [zeroX];

    if (Math.random() < 0.5) {
      graphKind = "linear";
      const slope = randInt(1, 2);
      rawFunction = (x) => orientation * slope * (x - zeroX);
    } else {
      graphKind = "quadratic";
      const outsideRoot =
        Math.random() < 0.5 ? xL - randInt(2, 4) : xR + randInt(2, 4);
      rawFunction = (x) => orientation * (x - zeroX) * (x - outsideRoot);
    }
  }

  const probeXs = [xL, xR, ...zeros];
  for (let x = xL; x <= xR; x += 0.25) probeXs.push(x);
  const maxAbs = Math.max(...probeXs.map((x) => Math.abs(rawFunction(x))), 1);
  const scale = graphKind === "linear" ? 1 : 4 / maxAbs;
  const fn = (x) => rawFunction(x) * scale;

  const endpointValues = [fn(xL), fn(xR)];
  if (endpointValues.some((value) => Math.abs(value) < 1)) return null;
  if (endpointValues.some((value) => Math.abs(value) > 5)) return null;

  const signSectionValues = [];
  const signBoundaries = [xL, ...zeros, xR];
  for (let index = 0; index < signBoundaries.length - 1; index += 1) {
    const left = signBoundaries[index];
    const right = signBoundaries[index + 1];
    signSectionValues.push(Math.abs(fn((left + right) / 2)));
  }
  if (signSectionValues.some((value) => value < 1)) return null;

  const boundaries = [xL, ...zeros, xR];
  const signs = boundaries.slice(0, -1).map((left, index) => {
    const right = boundaries[index + 1];
    return fn((left + right) / 2) > 0;
  });

  const answerIntervals = [];
  signs.forEach((isPositive, index) => {
    if (isPositive !== relation.wantsPositive) return;

    const isDomainLeft = index === 0;
    const isDomainRight = index === signs.length - 1;
    answerIntervals.push(
      makeInterval(
        boundaries[index],
        boundaries[index + 1],
        isDomainLeft ? leftClosed : relation.includeZeros,
        isDomainRight ? rightClosed : relation.includeZeros
      )
    );
  });

  return {
    relation,
    graphKind,
    zeros,
    answerIntervals,
    points: {
      A: {
        x: xL,
        y: fn(xL),
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Z: {
        x: xR,
        y: fn(xR),
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    },
    curves: [sampleFunction(fn, xL, xR, zeros)],
    xRange: [-6, 6],
    yRange: [-6, 6],
  };
}

function buildData() {
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const data = tryBuildData();
    if (data) return data;
  }
  return buildData();
}

function intervalLatex(value) {
  return `${value.leftClosed ? "[" : "("}${value.left},\\,${value.right}${
    value.rightClosed ? "]" : ")"
  }`;
}

function answerLatex(intervals) {
  return intervals.map(intervalLatex).join("\\cup");
}

export function generateTask() {
  return { type: "ReadSignIntervalFromGraphTask", data: buildData() };
}

export function renderTask({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <p>
        Na wykresie przedstawiono funkcję. Odczytaj zbiór wszystkich rozwiązań
        nierówności <InlineMath math={data.relation.latex} />.
      </p>
      <div style={{ margin: "0 auto", maxWidth: 330 }}>
        <GraphViewer
          width={330}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <div>
        Dla nierówności <InlineMath math={data.relation.latex} /> zapisz rozwiązanie
        w postaci <InlineMath math={"x\\in\\_\\_\\_\\_"} />.
      </div>
    </div>
  );
}

export function renderInput({ data }, userInput = {}, onChange, disabled) {
  const raw = getIntervalRaw(userInput, "I");
  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Rozwiązanie nierówności:</strong>
      </div>
      <div><InlineMath math={`${data.relation.latex}:\\quad x\\in`} /></div>
      <div>
        <IntervalInput
          value={userInput.I ?? ""}
          onChange={(value) => onChange({ ...(userInput || {}), I: value })}
          allowUnion
          disabled={disabled}
          showPreview={false}
        />
      </div>
      {raw ? <InlineMath math={`x\\in ${raw}`} /> : null}
    </div>
  );
}

export function checkAnswer({ data }, userInput = {}) {
  const raw = getIntervalRaw(userInput, "I");
  const parsed =
    data.answerIntervals.length === 1
      ? parseSingleInterval(raw)
      : parseIntervalsUnion(raw);
  if (!parsed) return false;
  return intervalsEqualList(
    data.answerIntervals,
    Array.isArray(parsed) ? parsed : [parsed]
  );
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${answerLatex(data.answerIntervals)}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Odczytujemy miejsca zerowe wykresu:{" "}
        <InlineMath math={data.zeros.join(",\\;")} />.
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Wybieramy fragmenty wykresu leżące{" "}
        {data.relation.wantsPositive ? "nad" : "pod"} osią <InlineMath math={"OX"} />.
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>Krok 3.</strong>{" "}
        {data.relation.includeZeros
          ? "Znak równości oznacza, że miejsca zerowe należą do rozwiązania."
          : "Nierówność ostra oznacza, że miejsca zerowe nie należą do rozwiązania."}
      </div>
      <div style={{ width: "min(100%, 620px)", margin: "12px auto" }}>
        <MaturaPiecewiseInequalityExplanationGraph
          points={data.points}
          curves={data.curves}
          solutionIntervals={data.answerIntervals}
          threshold={0}
          operator={data.relation.operator}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <div style={{ marginTop: 10 }}>
        <InlineMath math={`x\\in ${answerLatex(data.answerIntervals)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = getIntervalRaw(userAnswer, "I");
  return raw ? <InlineMath math={`x\\in ${raw}`} /> : <i>(brak odpowiedzi)</i>;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
